import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Typography, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, IconButton, 
  Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, Tooltip, TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth, API_BASE_URL } from '../../context/AuthContext';

const GIORNI = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

// Estrae HH:MM da una stringa "HH:MM - HH:MM"
const parseTimeSlot = (time_slot) => {
  if (!time_slot) return { start: '', end: '' };
  const parts = time_slot.split(' - ');
  return {
    start: parts[0]?.trim() || '',
    end: parts[1]?.trim() || '',
  };
};

export default function SchedulesAdmin() {
  const [schedules, setSchedules] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    day_of_week: 'Lunedì',
    start_time: '',
    end_time: '',
  });

  const { user } = useAuth();

  const fetchSchedules = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/schedules.php`);
      const data = await response.json();
      if(Array.isArray(data)) setSchedules(data);
    } catch (error) {
      console.error("Errore fetch schedules:", error);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  const handleOpenForm = (schedule = null) => {
    if (schedule) {
      setEditingId(schedule.id);
      const { start, end } = parseTimeSlot(schedule.time_slot);
      setFormData({
        day_of_week: schedule.day_of_week,
        start_time: start,
        end_time: end,
      });
    } else {
      setEditingId(null);
      setFormData({ day_of_week: 'Lunedì', start_time: '', end_time: '' });
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => setOpenForm(false);

  const handleSave = async () => {
    if (!formData.start_time || !formData.end_time) {
      alert("Seleziona orario di inizio e fine.");
      return;
    }

    const time_slot = `${formData.start_time} - ${formData.end_time}`;
    const payload = {
      day_of_week: formData.day_of_week,
      time_slot,
      ...(editingId && { id: editingId }),
    };

    const method = editingId ? 'PUT' : 'POST';
    const token = localStorage.getItem('adminToken');

    try {
      const response = await fetch(`${API_BASE_URL}/schedules.php`, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        handleCloseForm();
        fetchSchedules();
      } else {
        alert("Errore durante il salvataggio.");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo orario?")) return;
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/schedules.php`, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id })
      });
      if (response.ok) {
        fetchSchedules();
      } else {
        alert("Errore durante l'eliminazione.");
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Gestione Orari</Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenForm()}>
          + Aggiungi Orario
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Giorno</TableCell>
              <TableCell>Orario</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.day_of_week}</TableCell>
                <TableCell>{row.time_slot}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Modifica orario">
                    <IconButton color="primary" onClick={() => handleOpenForm(row)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Elimina orario">
                    <IconButton color="error" onClick={() => handleDelete(row.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {schedules.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">Nessun orario presente.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modale per Aggiunta/Modifica */}
      <Dialog open={openForm} onClose={handleCloseForm} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Modifica Orario' : 'Nuovo Orario'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Select Giorno */}
            <FormControl fullWidth required>
              <InputLabel id="giorno-label">Giorno della settimana</InputLabel>
              <Select
                labelId="giorno-label"
                value={formData.day_of_week}
                label="Giorno della settimana"
                onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
              >
                {GIORNI.map((g) => (
                  <MenuItem key={g} value={g}>{g}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Orari inizio / fine */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
              <TextField
                label="Ora inizio"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                onClick={(e) => {
                  try {
                    if (typeof e.target.showPicker === 'function') {
                      e.target.showPicker();
                    }
                  } catch (err) {}
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                fullWidth
              />
              <Typography sx={{ mt: 1 }}>—</Typography>
              <TextField
                label="Ora fine"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                onClick={(e) => {
                  try {
                    if (typeof e.target.showPicker === 'function') {
                      e.target.showPicker();
                    }
                  } catch (err) {}
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                fullWidth
              />
            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Annulla</Button>
          <Button onClick={handleSave} variant="contained">Salva</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
